import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Icon from 'components/Icons/Icon';
import { Tooltip } from 'components/Common/Tooltip/Tooltip';
import { formatDateTimeData } from './formatDateTimeData';
import CopyToClipboard from 'components/Site/SiteView/SiteOverview/Tabs/CopyToClipboard';
import { Row, Col, Form, Modal, Card, Container } from "react-bootstrap";

function DateTimeFormatUtility({
  types = [],
  dateTime = [],
  timezone,
  windowSizeProp,
  layout = "flex-column",
  showTimezone = true,
  showIcons = true,
  minimised = false,
  descriptionPrefix,
  tooltipPlacement = 'left',
  useDateTimeSmall = false,
  showClipboard = false,
  customClipboardLocation = false,
  showLabel = false,
}) {

  const darkMode = useSelector((state) => state.app.darkMode)

  const [formattedData, setFormattedData] = useState({});
  const [dateTimeFormatSmall, setDateTimeFormatSmall] = useState(false);
  const [windowSize, setWindowSize] = useState({
      width: window.innerWidth,
      height: window.innerHeight,
    });

  useEffect(() => {
    if (!windowSizeProp) {
      const handleResize = () => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
        setDateTimeFormatSmall(useDateTimeSmall && window.innerWidth < 550);
      };

      window.addEventListener('resize', handleResize);
      // Initialize on mount
      handleResize();
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [windowSizeProp, useDateTimeSmall]);


  useEffect(() => {
    const data = formatDateTimeData(types, dateTime, timezone, windowSize);
    setFormattedData(data);
  }, [types, dateTime, timezone, windowSize, setFormattedData]);

  return (
    layout === "flex-row" && dateTimeFormatSmall ?
      <></>
    :
      <div className={`date-time-wrapper ${darkMode ? "darkMode" : ""}`}>
        {showTimezone && <div className='d-flex justify-content-between align-items-center pl-1 pr-1 timezone-header'>
          {dateTimeFormatSmall ? (
            <Tooltip placement={tooltipPlacement} description='Timezone'>
              <span className='m-1'><i className="fas fa-globe" /></span>
            </Tooltip>
          ) : (
            <span className='font-weight-bold mr-2'>Timezone:</span>
          )}
          <span className='mr-1'>{timezone}</span>
        </div>}

        <div className={layout === "flex-row" ? "date-time-row" : "date-time"}>
          {/* Created */}
          {showLabel && 
            <div className={`d-flex flex-row align-items-center ${showClipboard && !customClipboardLocation ? "detail-field p-0 m-0" : ""}`}>
              <Form.Label className="form-input-labels m-0">Created</Form.Label>
              {showClipboard && <CopyToClipboard id={"date-time-format-utility-created"} copyText={formattedData?.created?.small} />}
            </div>
          }
          {formattedData.created && (
            <Tooltip 
              placement={tooltipPlacement}
              description={
              minimised ? `${descriptionPrefix || ""}${descriptionPrefix ? " c" : "C"}reated on ${formattedData.created.day} ${formattedData.created.month} ${formattedData.created.year} at ${formattedData.created.time}`
              : `${descriptionPrefix || ""}${descriptionPrefix ? " c" : "C"}reated`
              }
            >
                <div className={`date-time-section ${layout === 'flex-row' && 'p-0'}`} id={`${layout === "flex-row" ? "created-date-time-no-border" : "created-date-time"}`}>
                  {showIcons && 
                    <span className={`date-time-icon ${dateTimeFormatSmall || (minimised && 'mr-0 p-0')}`}>
                      <Icon className="fas fa-cloud-upload-alt" />
                    </span>
                  }

                  {formattedData.created.small && (dateTimeFormatSmall || minimised) ? (
                    <div className='date-time-small'>
                      <span className={`${showIcons && 'ml-1'}`}>{formattedData.created.small}</span>
                      {showClipboard && <CopyToClipboard id={"date-time-format-utility-created"} copyText={formattedData?.created?.small} />}
                    </div>
                  ) : (
                    <div className='date-time-contents'>
                      <span className='date-chunk'>{formattedData.created.day}</span>
                      <span className='date-chunk'>{formattedData.created.month}</span>
                      <span className='date-chunk'>{formattedData.created.year}</span>
                    </div>
                  )}

                  {minimised ?
                    <></>
                  :
                    layout === "flex-row" && !dateTimeFormatSmall &&
                      <>
                        <span>-</span>
                        <span className={dateTimeFormatSmall || layout === "flex-row" ? 'm-1' : 'ml-3'}>
                          {formattedData.created.time}
                        </span>
                      </>
                  }
                </div>
            </Tooltip>
          )}

          {/* Modified */}
          {formattedData.modified && (
            <Tooltip 
              placement={tooltipPlacement}
              description={
              minimised ? `${descriptionPrefix || ""}${descriptionPrefix ? " last modified" : "Last Modified"} on ${formattedData.modified.day} ${formattedData.modified.month} ${formattedData.modified.year} at ${formattedData.modified.time}`
              : `${descriptionPrefix || ""}${descriptionPrefix ? " l" : "L"}ast Modified`
              }
            >
              <div 
                className={`date-time-section ${layout === 'flex-row' && 'p-0'}`} 
                id={`${layout === "flex-row" ? "modified-date-time-no-border" : "modified-date-time"}`}
              >
                {showIcons && 
                  <span className={`date-time-icon ${dateTimeFormatSmall || minimised && 'mr-0 p-0'}`}>
                    <Icon className="fas fa-clock" />
                  </span>
                }

                {formattedData.modified.small && (dateTimeFormatSmall || minimised) ? (
                  <div className='date-time-small'>
                    <span className={`${showIcons && 'ml-1'}`}>{formattedData.modified.small}</span>
                    {showClipboard && <CopyToClipboard id={"date-time-format-utility-modified"} copyText={formattedData?.modified?.small} />}
                  </div>
                ) : (
                  <div className='date-time-contents'>
                    <span className='date-chunk'>{formattedData.modified.day}</span>
                    <span className='date-chunk'>{formattedData.modified.month}</span>
                    <span className='date-chunk'>{formattedData.modified.year}</span>
                  </div>
                )}

                {minimised ?
                  <></>
                :
                  layout === "flex-row" && !dateTimeFormatSmall &&
                  <>
                    <span>-</span>
                    <span className={dateTimeFormatSmall || layout === "flex-row" ? 'm-1' : 'ml-3'}>
                      {formattedData.modified.time}
                    </span>
                  </>
                }
              </div>
            </Tooltip>
          )}
        </div>
      </div>
  );
}

export default DateTimeFormatUtility;
